// Importaciones de interfaces.
import { IConfiguration } from '../interfaces/configuration.interface';

/**
 * Clase que representa una configuración.
 *
 * Esta clase contiene información relevante sobre una configuración, incluyendo
 * el identificador, el nombre, el valor, fecha de modificación y de creación.
 */
export class Configuration implements IConfiguration {
  /**
   * Identificador único de la configuración.
   *
   * Este campo representa un identificador único asignado a la configuración.
   * Puede ser un número o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Nombre de la configuración.
   *
   * Este campo almacena el nombre de la configuración.
   * Puede ser nulo si no se ha establecido.
   */
  name: string | null;

  /**
   * Valor de la configuración.
   *
   * Este campo define el valor de la configuración.
   * Puede ser nulo si no se ha establecido.
   */
  value: string | null;

  /**
   * Fecha de última actualización.
   *
   * Indica el momento en que la configuración fue modificada por última vez.
   * Es útil para el seguimiento de cambios y auditorías.
   */
  updatedAt: string | null;

  /**
   * Fecha de creación.
   *
   * Representa el instante en que la configuración fue registrada por primera vez.
   * Es importante para conocer su antigüedad y gestionar su validez.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de `IConfiguration`.
   *
   * @param {Partial<IConfiguration>} configuration - Un objeto parcial de `IConfiguration` con las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador de la configuración. Por defecto, `null`.
   *   - `name` (opcional): Nombre de la configuración. Por defecto, `null`.
   *   - `valor` (opcional): Valor de la configuración. Por defecto, `null`.
   *   - `updatedAt` (opcional): Fecha de última actualización. Por defecto, `null`.
   *   - `createdAt` (opcional): Fecha de creación. Por defecto, `null`.
   */
  constructor({
    id = null,
    name = null,
    value = null,
    updatedAt = null,
    createdAt = null,
  }: Partial<IConfiguration> = {}) {
    this.id = id;
    this.name = name;
    this.value = value;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
